import {
  ApolloClient,
  type ApolloError,
  ApolloLink,
  type DocumentNode,
  InMemoryCache,
  type OperationVariables,
  type ServerError,
  type ServerParseError,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import { normalize, type Schema } from 'normalizr'
import * as R from 'ramda'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import {
  ApiError,
  ErrorTypes,
  getSessionId,
  Utils,
} from '@pbt/pbt-ui-components'
import {
  Environment,
  Region,
} from '@pbt/pbt-ui-components/src/constants/environment'
import { getDomainContext } from '@pbt/pbt-ui-components/src/utils'

import { getBusinessIdFromSessionStorage } from '../utils/businessId'
import { tokenHolder } from '../utils/token'
import { getGraphQLWsUrl } from './graphqlSubscriptions'

// cached WS link and last used URL are declared early so lifecycle helpers
// can operate on them without ordering issues (lint: no-use-before-define).
let cachedWsLink: WebSocketLink | null = null
let lastWsUrl: string | null = null

let activeSubscriptionClient: SubscriptionClient | null = null

export const refreshGraphQLSubscriptionsAuth = () => {
  try {
    activeSubscriptionClient?.close(false, false)
  } catch {
    // intentionally empty: best-effort close
  }
  cachedWsLink = null
}

export const closeGraphQLSubscriptions = () => {
  try {
    activeSubscriptionClient?.close(true, true)
  } catch {
    // intentionally empty: best-effort close
  } finally {
    activeSubscriptionClient = null
    cachedWsLink = null
  }
}

const PublicApiMap: Record<Region, Partial<Record<Environment, string>>> = {
  [Region.US]: {
    [Environment.PROD]: 'https://api.rhapsody.vet',
    [Environment.MASTER]: 'https://api.master.rhapsody.vet',
    [Environment.STAGE]: 'https://api.stage.rhapsody.vet',
  },
  [Region.EU]: {
    [Environment.PROD]: 'https://api.eu.rhapsody.vet',
    [Environment.MASTER]: 'https://api.eu.master.rhapsody.vet',
    [Environment.STAGE]: 'https://api.eu.stage.rhapsody.vet',
  },
}

type GQLRequestContext = {
  headers: Record<string, string | boolean | null>
}

const domainContext = getDomainContext()
const envApiMap = PublicApiMap[domainContext.region]
const defaultApiUrl = envApiMap[Environment.STAGE]
const apiUrl = envApiMap[domainContext.env] || defaultApiUrl

export const buildGraphQLOperationUrl = (operationName: string): string => {
  const base = Utils.isLocalEnvironment() ? '' : apiUrl
  return `${base}/api-public/${operationName}`
}

// Cast needed: @types/apollo-upload-client bundles @apollo/client@3.11.8 types
// which conflict with our 3.14 ApolloLink type.
const uploadLink = createUploadLink({
  uri: ({ operationName }) => buildGraphQLOperationUrl(operationName),
}) as unknown as ApolloLink

export const buildGraphQLHeaders = async (
  existingHeaders?: Record<string, any>,
) => {
  const token = await tokenHolder.getToken()

  return {
    ...existingHeaders,
    Authorization: token ? `Bearer ${token}` : '',
    // eslint-disable-next-line pbt-portal/no-localstorage-businessid -- Redux not accessible in Apollo link context.
    'X-Business-Id': getBusinessIdFromSessionStorage(),
    'X-PBT-SessionId': getSessionId(),
  }
}

const authLink = setContext(async (_, { headers }) => ({
  headers: await buildGraphQLHeaders(headers),
}))
const link = ApolloLink.from([authLink, uploadLink])

/*
  GraphQL Subscriptions lifecycle summary

  - Authentication
    - After successful login, call `refreshGraphQLSubscriptionsAuth()` to force
      the subscriptions client to reconnect and re-run `connectionParams` with a
      fresh access token.
    - On logout, call `closeGraphQLSubscriptions()` to fully terminate the WS
      and prevent auto-reconnect. Unauthenticated sessions hold no WS socket.

  - Environment routing (local dev)
    - The WS URL is derived from `getGraphQLWsUrl()` which honors
      `window.pbt.getDevEnv()/getDevRegion()` and appends
      `pbtDevEnv/pbtDevRegion` query params for correct gateway routing.
    - If the computed WS URL changes at runtime (e.g., switching master/stage),
      we trigger a silent page reload to rebuild links against the new endpoint.

  - Connection policy
    - `lazy: true` and `inactivityTimeout` keep the socket closed until a
      subscription is used, and close idling sockets automatically.
*/

const canUseWebSocket =
  typeof window !== 'undefined' &&
  typeof (window as any).WebSocket !== 'undefined'

export const buildWebSocketConnectionParams = async (): Promise<
  Record<string, string | null>
> => {
  const token = await tokenHolder.getToken()
  const headers: Record<string, string | null> = {
    Authorization: token ? `Bearer ${token}` : '',
    // eslint-disable-next-line pbt-portal/no-localstorage-businessid -- Redux not accessible in WebSocket connection params.
    'X-Business-Id': getBusinessIdFromSessionStorage(),
    'X-PBT-SessionId': getSessionId(),
  }

  if (Utils.isLocalEnvironment()) {
    try {
      const devEnv = (window as any)?.pbt?.getDevEnv?.()
      const devRegion = (window as any)?.pbt?.getDevRegion?.()
      if (devEnv) headers['X-PBT-DevEnv'] = String(devEnv)
      if (devRegion) headers['X-PBT-DevRegion'] = String(devRegion)
    } catch {
      // intentionally ignore; keep headers as-is
    }
  }

  return headers
}

const createWsLinkSafely = () => {
  try {
    const wsUrl = getGraphQLWsUrl()
    if (!canUseWebSocket || !wsUrl) {
      return null
    }

    const subscriptionClient = new SubscriptionClient(wsUrl, {
      reconnect: true,
      minTimeout: 2000,
      lazy: true,
      inactivityTimeout: 30000,
      connectionParams: buildWebSocketConnectionParams,
    })
    activeSubscriptionClient = subscriptionClient
    lastWsUrl = wsUrl
    return new WebSocketLink(subscriptionClient)
  } catch {
    return null
  }
}

export const shouldReloadForUrlChange = (
  lastUrl: string | null,
  currentUrl: string | null,
): boolean => Boolean(lastUrl && currentUrl && lastUrl !== currentUrl)

const wsDelegatingLink = new ApolloLink((operation) => {
  // Lazily create WS link with up-to-date URL/env; detect env URL changes
  const currentUrl = getGraphQLWsUrl()
  if (shouldReloadForUrlChange(lastWsUrl, currentUrl)) {
    // silent reload on URL change
    window.location.reload()
    return null
  }

  if (!cachedWsLink) {
    const created = createWsLinkSafely()
    if (created) {
      cachedWsLink = created
    }
  }
  return cachedWsLink ? cachedWsLink.request(operation) : null
})

export const isSubscriptionOperation = (query: DocumentNode): boolean => {
  const definition = getMainDefinition(query)
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  )
}

const splitLink = ApolloLink.split(
  ({ query }) => isSubscriptionOperation(query),
  wsDelegatingLink,
  link,
)

export const clientForApolloFlow = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      ConstantEntity: {
        keyFields: false,
      },
    },
  }),

  devtools: {
    enabled: !Utils.isProduction(),
    name: 'Apollo Client',
  },

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})

export const clientForLegacyReduxFlow = new ApolloClient({
  link: uploadLink,

  cache: new InMemoryCache({
    addTypename: false,
    resultCaching: false,
  }),

  devtools: {
    enabled: !Utils.isProduction(),
    name: 'Legacy Redux Apollo',
  },

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
    mutate: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
  },
})

const convertApolloToApiError = (error: ApolloError): ApiError =>
  new ApiError(
    {
      message: error.message,
      response:
        (error.networkError as ServerParseError | ServerError)?.response || {},
    },
    {},
  )

export const getRequestContext = async (businessId: string, auth: boolean) => {
  const context: GQLRequestContext = {
    headers: {},
  }

  if (Utils.isLocalEnvironment()) {
    const devEnv = window.pbt.getDevEnv() as Environment
    const devRegion = window.pbt.getDevRegion() as Region

    context.headers = {
      ...context.headers,
      'X-PBT-DevEnv': devEnv,
      'X-PBT-DevRegion': devRegion,
    }
  }

  if (auth) {
    const token = await tokenHolder.getToken()

    context.headers = {
      ...context.headers,
      Authorization: `Bearer ${token}`,
      'X-Business-Id': businessId,
      'X-PBT-SessionId': getSessionId(),
    }
  }

  return context
}

interface CommonQueryParameters<P, T extends P[]> {
  auth?: boolean
  businessIdHeaderOverrider?: (
    currentBusinessId: string,
    ...restVariables: T
  ) => string
  firstDataAliasPick?: boolean
  schema?: Schema
  variablesHandler?: (
    currentBusinessId: string,
    ...restVariables: T
  ) => OperationVariables
}

interface RequestQuery<P, T extends P[]> extends CommonQueryParameters<P, T> {
  expectedErrors?: string[]
  query: DocumentNode
}

interface RequestMutation<P, T extends P[]> extends CommonQueryParameters<
  P,
  T
> {
  mutation: DocumentNode
}

export const requestQuery =
  <P, T extends P[]>({
    query,
    variablesHandler,
    schema,
    auth = true,
    firstDataAliasPick = true,
    businessIdHeaderOverrider = R.identity as any,
    expectedErrors,
  }: RequestQuery<P, T>) =>
  async (currentBusinessId: string, ...restVariables: T) => {
    try {
      const variables = variablesHandler
        ? variablesHandler(currentBusinessId, ...restVariables)
        : undefined
      const businessIdHeader = businessIdHeaderOverrider(
        currentBusinessId,
        ...restVariables,
      )
      const { data, errors } = await clientForLegacyReduxFlow.query({
        query,
        variables,
        context: await getRequestContext(businessIdHeader, auth),
      })
      let friendlyErrors = {}
      if (expectedErrors && expectedErrors.length > 0 && errors) {
        if (
          errors.every((e) =>
            expectedErrors.includes(e.extensions?.type as string),
          )
        ) {
          friendlyErrors = errors
        } else {
          throw new ErrorTypes.GraphQLError(R.head(errors), query, variables)
        }
      }
      const preparedData = firstDataAliasPick
        ? data[Object.keys(data)[0]]
        : data
      if (schema) {
        return R.isEmpty(friendlyErrors)
          ? normalize(preparedData || {}, schema)
          : {
              errors: friendlyErrors,
              ...normalize(preparedData || {}, schema),
            }
      }
      return preparedData
    } catch (e) {
      if (e instanceof ErrorTypes.GraphQLError) {
        throw e
      }

      throw convertApolloToApiError(e as ApolloError)
    }
  }

export const requestMutation =
  <P, T extends P[]>({
    mutation,
    variablesHandler,
    schema,
    auth = true,
    firstDataAliasPick = true,
    businessIdHeaderOverrider = R.identity as any,
  }: RequestMutation<P, T>) =>
  async (currentBusinessId: string, ...restVariables: T) => {
    try {
      const variables = variablesHandler
        ? variablesHandler(currentBusinessId, ...restVariables)
        : undefined
      const businessIdHeader = businessIdHeaderOverrider(
        currentBusinessId,
        ...restVariables,
      )
      const { data, errors } = await clientForLegacyReduxFlow.mutate({
        mutation,
        variables,
        context: await getRequestContext(businessIdHeader, auth),
      })
      if (errors) {
        throw new ErrorTypes.GraphQLError(R.head(errors), mutation, {
          data,
          variables,
        })
      }
      const preparedData = firstDataAliasPick
        ? data[Object.keys(data)[0]]
        : data
      return schema ? normalize(preparedData || {}, schema) : preparedData
    } catch (e) {
      if (e instanceof ErrorTypes.GraphQLError) {
        throw e
      }

      throw convertApolloToApiError(e as ApolloError)
    }
  }
